// API Configuration
// Environment-based API URL configuration
const getApiBase = () => {
  // Use REACT_APP_API_URL from .env if set
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Fallbacks
  const devUrl = 'http://localhost:8000/api'; 
  const prodUrl = 'https://app.docxsai.com/api';
  
  return process.env.NODE_ENV === 'production' ? prodUrl : devUrl;
};

export const API_BASE = getApiBase();

/**
 * Optional public flag from GET /api/config (backend SUBSCRIPTIONS_ENABLED).
 * Paid CTAs are not gated on this — backend 403 is shown when checkout is disabled.
 * Defaults to true if the request fails.
 */
export const fetchSubscriptionsEnabled = async () => {
  try {
    const data = await apiRequest('config');
    return data?.subscriptions_enabled !== false;
  } catch {
    return true;
  }
};

// Helper function to make authenticated API requests
export const apiRequest = async (endpoint, options = {}) => {
  // Remove any leading slash from endpoint to avoid double slashes
  endpoint = endpoint.replace(/^\/+/, '');
  const url = `${getApiBase()}/${endpoint}`;
  const token = localStorage.getItem('docxsai-token');
  
  const headers = {
    Accept: 'application/json', // Always set Accept header
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Handle different body types (JSON vs FormData)
  if (options.body) {
    if (options.body instanceof FormData) {
      // Don't set Content-Type for FormData, browser does it with boundary
    } else if (typeof options.body === 'object') {
      options.body = JSON.stringify(options.body);
      headers['Content-Type'] = 'application/json';
    }
  }

  const config = {
    ...options,
    headers,
  };

  let response;
  try {
    response = await fetch(url, config);
  } catch (error) {
    throw new Error(
      error?.message?.toLowerCase().includes('failed to fetch')
        ? 'Unable to connect to the server. Please check your internet connection or try again later.'
        : (error?.message || 'Network error while contacting the server.')
    );
  }

  let responseData;
  try {
    responseData = await response.json();
  } catch (e) {
    responseData = { detail: 'Network error' };
  }

  if (!response.ok) {
    // Try to show the most relevant error message
    const errorMsg = responseData.detail || responseData.message || responseData.error || `HTTP ${response.status}`;
    throw new Error(errorMsg);
  }

  return responseData;
};

// Test API connection
export const testApiConnection = async () => {
  try {
    const response = await fetch(`${API_BASE}/health`);
    return response.ok;
  } catch (error) {
    console.error('API connection test failed:', error);
    return false;
  }
};

// Environment info for debugging (only in development)
export const getEnvironmentInfo = () => {
  const info = {
    nodeEnv: process.env.NODE_ENV,
    apiBase: API_BASE,
    hasCustomApiBase: !!process.env.REACT_APP_API_URL,
    reactAppApiUrl: process.env.REACT_APP_API_URL,
    allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
  };
  
  // Only log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Environment Debug Info:', info);
  }
  
  return info;
};

/** Ensure bare domains (e.g. example.com) become absolute https URLs for href/src use. */
export function normalizeExternalUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const value = url.trim();
  if (!value) return value;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return value;
  if (value.startsWith('//')) return `https:${value}`;
  return `https://${value.replace(/^\/+/, '')}`;
}